import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { addError } from "@wayflyer/blackbox-fe";
import type { BaseSchemas } from "api-types";
import type { AxiosError, AxiosResponse } from "axios";
import { useLayoutEffect, useState } from "react";
import { getQueryParameters } from "shared-utils";

export const COBRANDING_CONFIG_QUERY_KEY = "cobranding_config_uuid";
const CONFIFG_LOCAL_STORAGE_KEY = "cobranding_uuid";

export const useCobrandingConfig = (
  options: Omit<
    UseQueryOptions<
      AxiosResponse<BaseSchemas.CobrandingConfig> | null,
      AxiosError,
      BaseSchemas.CobrandingConfig | null
    >,
    "queryKey" | "queryFn" | "select"
  > = {}
): UseQueryResult<BaseSchemas.CobrandingConfig | null> => {
  const { [COBRANDING_CONFIG_QUERY_KEY]: cobrandingUUID } = getQueryParameters();

  const [activeUuid, setActiveUuid] = useState<string | null>(() => {
    try {
      return localStorage.getItem(CONFIFG_LOCAL_STORAGE_KEY);
    } catch (e) {
      addError(e);
      return null;
    }
  });

  useLayoutEffect(() => {
    if (cobrandingUUID && cobrandingUUID !== activeUuid) {
      localStorage.setItem(CONFIFG_LOCAL_STORAGE_KEY, cobrandingUUID);
      setActiveUuid(cobrandingUUID);
    }
  }, [cobrandingUUID, activeUuid]);

  const axiosClient = useAxios();

  return useQuery<AxiosResponse<BaseSchemas.CobrandingConfig> | null, AxiosError, BaseSchemas.CobrandingConfig | null>({
    queryKey: [COBRANDING_CONFIG_QUERY_KEY, activeUuid],
    queryFn: async () => {
      const cobrandingConfig = await axiosClient.get<BaseSchemas.CobrandingConfig>(`/cobranding/${activeUuid}`);
      return cobrandingConfig;
    },
    enabled: Boolean(activeUuid),
    staleTime: Infinity,
    gcTime: Infinity,
    select: (response) => response?.data || null,
    ...options,
  });
};
